import type { AsyncData } from '#app';
import type {
  IModelListQueryParamsRequest,
  IModelResponse,
} from '~/services/base/dto/base.dto';
import type {
  IModelCreateKnowledgeCategoryRequest,
  IModelKnowledgeCategoryResponse,
  IModelKnowledgeDetailResponse,
  IModelApplyFileStatusResponse,
  IModelSaveLinkResponse,
  IModelSaveLinkRequest,
  IModelValidateLinkRequest,
} from './dto/knowledge-base.dto';

export class KnowledgeBaseRepository {
  private api: any;
  private configService: any;
  private kmService: any;
  private kmBase: any;

  constructor(api: any, configService: any) {
    const runtimeConfig = useRuntimeConfig();
    this.api = api;
    this.configService = configService;
    this.kmService = runtimeConfig.public.apiKMService;
    this.kmBase = runtimeConfig.public.apiKMBase;
  }

  async getKnowledgeCategory(
    params: IModelListQueryParamsRequest
  ): Promise<
    AsyncData<IModelResponse<IModelKnowledgeCategoryResponse[]>, any>
  > {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-asso/category-knowledge`,
      this.configService,
      {
        baseUrl: this.kmBase,
        params,
      }
    );
    return dataResult;
  }

  async getKnowledgeCategoryById(
    id: string | number
  ): Promise<AsyncData<IModelResponse<IModelKnowledgeCategoryResponse>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-category${id}`,
      this.configService,
      {
        baseUrl: this.kmBase,
      }
    );
    return dataResult;
  }

  async createKnowledgeCategory(
    params: IModelCreateKnowledgeCategoryRequest
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-category`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async updateKnowledgeCategory(
    params: IModelCreateKnowledgeCategoryRequest
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-category`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'PUT',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async deleteKnowledgeCategory(
    id: string | number
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-category/${id}`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'DELETE',
      }
    );
    return dataResult;
  }

  async deleteKnowledge(
    id: string | number
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/knowledge/${id}`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'DELETE',
      }
    );
    return dataResult;
  }

  async updateKnowledge(
    params: any
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/knowledge/comment`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'PATCH',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async getKnowledgeDetail(
    view: 'knowledge-document' | 'knowledge-preset',
    params: IModelListQueryParamsRequest
  ): Promise<
    AsyncData<IModelResponse<IModelKnowledgeDetailResponse[]>, any>
  > {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-asso/${view}`,
      this.configService,
      {
        baseUrl: this.kmBase,
        params,
      }
    );
    return dataResult;
  }

  async uploadKnowledgeFile(
    formData: FormData
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/upload`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: formData,
      }
    );
    return dataResult;
  }

  async getApplyFileStatus(
    params: IModelListQueryParamsRequest
  ): Promise<AsyncData<IModelResponse<IModelApplyFileStatusResponse>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/file/apply/status`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async applyFile(
    params: IModelListQueryParamsRequest
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/file/apply`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async downloadKnowledgeFile(
    id: string | number
  ): Promise<AsyncData<any, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/file/download/${id}`,
      this.configService,
      {
        baseUrl: this.kmBase,
        responseType: 'blob',
      }
    );
    return dataResult;
  }

  async onSaveLink(
    params: IModelSaveLinkRequest
  ): Promise<AsyncData<IModelResponse<IModelSaveLinkResponse>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/link/save`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async getApplyLinkStatus(
    params: IModelListQueryParamsRequest
  ): Promise<AsyncData<IModelResponse<IModelApplyFileStatusResponse>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/link/apply/status`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

  async applyLink(
    params: IModelListQueryParamsRequest
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/link/apply`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }

   async validateLink(
    params: IModelValidateLinkRequest
  ): Promise<AsyncData<IModelResponse<any>, any>> {
    const dataResult = await this.api(
      `${this.kmService}/v1/knowledge-presenter/link/validate`,
      this.configService,
      {
        baseUrl: this.kmBase,
        method: 'POST',
        body: {
          data: params,
        },
      }
    );
    return dataResult;
  }
}
